import React, { useState, useEffect, useRef } from "react";
import { BACKEND } from "../config/configEnvs";
import axios from "axios";
import * as XLSX from 'xlsx';
import { FaCheckCircle, FaFileExcel, FaChartPie } from "react-icons/fa";
import Modal from "../components/ui/Modal";
import { toast } from "react-toastify";
import { FaSpinner } from "react-icons/fa";
import LoadingScreen from "../components/ui/temp-screens/LoadingScreen";
import NoDataScreen from "../components/ui/temp-screens/NoDataScreen";
import { FaTimes } from "react-icons/fa";
import FilterHeadersDialog from "../components/dialogs/FilterHeadersDialog";
import { Button } from "@mui/material";
import SearchIcon from '@mui/icons-material/Search';
import 'tippy.js/dist/tippy.css';
import Selector from "../components/ui/components/Selector";
import { months, years } from "../utils/rawData/dateValues";
import { FormatDateString } from "../utils/FormatDate";
import FilterButton from "../components/ui/components/FilterButton";
import ItemRowConciliacion from "../components/conciliacion/ItemRowConciliacion";
import ItemRowConciliacionResumen from "../components/conciliacion/ItemRowConciliacionResumen";
import BanksConciliacion from "../utils/rawData/banksConciliacion";

const Conciliacion = () => {

  const [detailSearch, setDetailSearch] = useState("")

  const [limit, setLimit] = useState(30)
  const [page, setPage] = useState(1)
  const debounceUpdate = useRef(null)

  const [openHeadersFilter, setOpenHeadersFilter] = useState(false)
  const [headersTableActive, setHeadersTableActive] = useState([]);

  const [record, setRecord] = useState([])
  const [isLoading, setIsLoading] = useState(false)

  const [bank, setBank] = useState("")
  const [month, setMonth] = useState(new Date().getMonth() + 1)
  const [year, setYear] = useState(new Date().getFullYear())

  const [vista, setVista] = useState("Registros")

  const [totalPages, setTotalPages] = useState(0)
  const [totalDocs, setTotalDocs] = useState(0)

  // Estados para totales de conciliación
  const [totalIngresos, setTotalIngresos] = useState(0)
  const [totalEgresos, setTotalEgresos] = useState(0)
  const [saldoNeto, setSaldoNeto] = useState(0)
  const [ingresosPorBanco, setIngresosPorBanco] = useState({})

  // Estado para datos de resumen
  const [resumenData, setResumenData] = useState([])

  const getRecord = async () => {
    try {
      setIsLoading(true)

      let url = `${BACKEND}/api/conciliacion?limit=0`
      
      if (detailSearch) {
        url += `&where[detail][like]=${detailSearch}`
      }
      if (bank) {
        url += `&where[bank][like]=${bank}`
      }
      if (month) {
        url += `&where[month][like]=${month}`
      }
      if (year) {
        url += `&where[year][like]=${year}`
      }

      const response = await axios.get(url)
      setRecord(response.data.docs)
      setTotalPages(response.data.totalPages)
      setTotalDocs(response.data.totalDocs)
      
      // Calcular totales de conciliación
      calcularTotalesConciliacion(response.data.docs)
      
      // Generar datos de resumen
      generarDatosResumen(response.data.docs)
      
    } catch (error) {
      toast.error("Error al obtener los datos")
      console.log(error)
    } finally {
      setIsLoading(false)
    }
  }

  const calcularTotalesConciliacion = (data) => {
    let totalIng = 0
    let totalEgr = 0
    let balanza = 0
    const bancosData = {}

    data.forEach(item => {
      // Convertir a números y manejar valores nulos/undefined
      const chargesOrCredits = parseFloat(item.chargesOrCredits) || 0
      const banco = item.bank || 'N/A'
      
      // Sumar ingresos (valores positivos de chargesOrCredits)
      if (chargesOrCredits > 0) {
        totalIng += chargesOrCredits
      }
      
      // Sumar egresos (valores negativos de chargesOrCredits, convertidos a positivo para mostrar)
      if (chargesOrCredits < 0) {
        totalEgr += Math.abs(chargesOrCredits)
      }

      // Calcular balanza (suma algebraica de todos los valores)
      balanza += chargesOrCredits

      // Agrupar datos por banco
      if (!bancosData[banco]) {
        bancosData[banco] = {
          ingresos: 0,
          egresos: 0,
          balanza: 0
        }
      }

      if (chargesOrCredits > 0) {
        bancosData[banco].ingresos += chargesOrCredits
      } else if (chargesOrCredits < 0) {
        bancosData[banco].egresos += Math.abs(chargesOrCredits)
      }
      
      bancosData[banco].balanza += chargesOrCredits
    })

    setTotalIngresos(totalIng)
    setTotalEgresos(totalEgr)
    setSaldoNeto(balanza)
    setIngresosPorBanco(bancosData)
  }

  const generarDatosResumen = (data) => {
    const resumenPorMes = {}

    data.forEach(item => {
      const mes = item.month || new Date(item.operationDate).getMonth() + 1
      const año = item.year || new Date(item.operationDate).getFullYear()
      const banco = item.bank || 'N/A'
      
      const key = `${mes}-${año}`
      
      if (!resumenPorMes[key]) {
        resumenPorMes[key] = {
          month: mes,
          year: año,
          totalInterbankIngresos: 0,
          totalInterbankEgresos: 0,
          totalInterbankBalanza: 0,
          totalBCPIngresos: 0,
          totalBCPEgresos: 0,
          totalBCPSaldoNeto: 0
        }
      }

      // Convertir a números y manejar valores nulos/undefined
      const chargesOrCredits = parseFloat(item.chargesOrCredits) || 0
      
      // Procesar ingresos y egresos por banco
      if (banco === 'Interbank') {
        if (chargesOrCredits > 0) {
          resumenPorMes[key].totalInterbankIngresos += chargesOrCredits
        }
        if (chargesOrCredits < 0) {
          resumenPorMes[key].totalInterbankEgresos += Math.abs(chargesOrCredits)
        }
      } else if (banco === 'BCP') {
        if (chargesOrCredits > 0) {
          resumenPorMes[key].totalBCPIngresos += chargesOrCredits
        }
        if (chargesOrCredits < 0) {
          resumenPorMes[key].totalBCPEgresos += Math.abs(chargesOrCredits)
        }
      }
    })

    // Calcular balances (suma algebraica de todos los valores)
    Object.values(resumenPorMes).forEach(resumen => {
      resumen.totalInterbankBalanza = resumen.totalInterbankIngresos - resumen.totalInterbankEgresos
      resumen.totalBCPSaldoNeto = resumen.totalBCPIngresos - resumen.totalBCPEgresos
    })

    // Convertir a array y ordenar por año y mes
    const resumenArray = Object.values(resumenPorMes).sort((a, b) => {
      if (a.year !== b.year) {
        return b.year - a.year
      }
      return b.month - a.month
    })

    setResumenData(resumenArray)
  }

  const handleSearch = () => {
    setPage(1)
    getRecord()
  }

  const handleResetSearch = () => {
    setDetailSearch("")
  }

  useEffect(() => {
    if (detailSearch === "") {
      getRecord()
      setPage(1)
    }
  }, [detailSearch])

  useEffect(() => {
    getRecord()
  }, [limit, page, bank, month, year])

  useEffect(() => {
    getRecord()
  }, [])
  
  const headers = [
    { id: 1, name: "Fecha de operación", content: "operationDate", formatter: FormatDateString, show: true },
    { id: 2, name: "Fecha de procesamiento", content: "processingDate", formatter: FormatDateString, show: true },
    { id: 3, name: "Movimiento", content: "movementType", show: true },
    { id: 4, name: "Detalle", content: "detail", show: true },
    { id: 5, name: "Código de operación", content: "operationCode", show: true },
    { id: 6, name: "Canal", content: "channel", show: true },
    { id: 7, name: "Codigo de ubicación", content: "locationCode", show: true },
    { id: 8, name: "Abonnos / Cargos", content: "chargesOrCredits", formatter: (value) => value.toLocaleString("es-PE", { minimumFractionDigits: 2, maximumFractionDigits: 2 }), show: true },
    { id: 9, name: "Salida", content: "amount", formatter: (value) => value.toLocaleString("es-PE", { minimumFractionDigits: 2, maximumFractionDigits: 2 }), show: true },
    { id: 10, name: "Justificación", content: "justification", input: true, show: true },
    { id: 11, name: "Banco", content: "bank", show: true },
  ]

  const headersResume = [
    { id: 1, name: "Mes", content: "month" },
    { id: 2, name: "Año", content: "year" },
    { id: 3, name: "Total Interbank Ingresos", content: "totalInterbankIngresos" },
    { id: 4, name: "Total Interbank Egresos", content: "totalInterbankEgresos" },
    { id: 5, name: "Total Interbank Balanza", content: "totalInterbankBalanza" },
    { id: 6, name: "Total BCP ingresos", content: "totalBCPIngresos" },
    { id: 7, name: "Total BCP egresos", content: "totalBCPEgresos" },
    { id: 8, name: "Total BCP balanza", content: "totalBCPSaldoNeto" },
  ]
  
  const [uploadModal, setUploadModal] = useState(false)
  const [excelUploaded, setExcelUploaded] = useState(null)
  const [fileValidation, setFileValidation] = useState({
    isValid: false,
    missingColumns: [],
    fileName: "",
    fileSize: 0,
    columns: []
  })
  const [isProcessingFile, setIsProcessingFile] = useState(false)

  const verifyColumns = ({columns, data}) => {
    const requiredColumns = ["FECHA DE OP.", "FECHA DE PROC.", "MOVIMIENTO", "DETALLE", "CODIGO DE OPERACION", "CANAL", "CODIGO UBICACION", "ABONOS/CARGOS", "SALIDA", "JUSTIFICACION", "BANCO"]

    // Normalizar las columnas del archivo para comparación (sin comillas)
    const normalizedColumns = columns.map(col => col.toString().trim())
    
    const missingColumns = requiredColumns.filter(column => !normalizedColumns.includes(column))

    if (missingColumns.length > 0) {
      return {
        isValid: false,
        missingColumns: missingColumns
      }
    }

    return {
      isValid: true,
      missingColumns: []
    }
  }

  const handleFileUpload = (file) => {
    if (!file) return

    // Verificar tipo de archivo
    const validTypes = [
      'application/vnd.ms-excel',
      'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
      'text/csv'
    ]

    if (!validTypes.includes(file.type)) {
      toast.error("Solo se permiten archivos Excel (.xlsx, .xls) o CSV")
      return
    }

    // Verificar tamaño (máximo 10MB)
    const maxSize = 10 * 1024 * 1024 // 10MB
    if (file.size > maxSize) {
      toast.error("El archivo es demasiado grande. Máximo 10MB")
      return
    }

    setIsProcessingFile(true)

    const reader = new FileReader()
    reader.onload = (e) => {
      try {
        const data = e.target.result
        const workbook = XLSX.read(data, { type: 'binary' })
        const sheetName = workbook.SheetNames[0]
        const worksheet = workbook.Sheets[sheetName]
        
        // Convertir a JSON con objetos (no arrays)
        const jsonData = XLSX.utils.sheet_to_json(worksheet, { header: 1 })
        
        if (jsonData.length < 2) {
          toast.error("El archivo no contiene datos válidos")
          setIsProcessingFile(false)
          return
        }

        // Obtener columnas (primera fila)
        const columns = jsonData[0]
        const dataRows = jsonData.slice(1)

        // Convertir arrays a objetos usando las columnas como keys y filtrar filas vacías
        const formattedData = dataRows
          .map(row => {
            const obj = {}
            columns.forEach((column, index) => {
              // Normalizar el nombre de la columna y asegurar formato con comillas dobles
              const normalizedColumn = column.toString().trim()
              // Crear la key con comillas dobles
              const columnKey = `"${normalizedColumn}"`
              obj[columnKey] = row[index] || ''
            })
            return obj
          })
          .filter(row => {
            // Verificar si la fila tiene al menos un valor no vacío
            return Object.values(row).some(value => 
              value !== '' && 
              value !== null && 
              value !== undefined && 
              value.toString().trim() !== ''
            )
          })

        // Verificar columnas
        const validation = verifyColumns({ columns, data: formattedData })

        setFileValidation({
          isValid: validation.isValid,
          missingColumns: validation.missingColumns,
          fileName: file.name,
          fileSize: file.size,
          columns: columns
        })

        setExcelUploaded({
          file: file,
          columns: columns,
          data: formattedData
        })

      } catch (error) {
        console.log(error)
        toast.error("Error al procesar el archivo")
      } finally {
        setIsProcessingFile(false)
      }
    }

    reader.onerror = () => {
      toast.error("Error al leer el archivo")
      setIsProcessingFile(false)
    }

    reader.readAsBinaryString(file)
  }

  const handleDragOver = (e) => {
    e.preventDefault()
  }

  const handleDrop = (e) => {
    e.preventDefault()
    const files = e.dataTransfer.files
    if (files.length > 0) {
      handleFileUpload(files[0])
    }
  }

  const handleFileInput = (e) => {
    const files = e.target.files
    if (files.length > 0) {
      handleFileUpload(files[0])
    }
  }

  const uploadExcel = async () => {

    if (!excelUploaded || !fileValidation.isValid) {
      toast.error("Por favor, selecciona un archivo válido")
      return
    }

    try { 
      setIsProcessingFile(true)

      // Mapeo de columnas del Excel (español) a campos del backend (inglés)
      const columnMapping = {
        "FECHA DE OP.": "operationDate",
        "FECHA DE PROC.": "processingDate", 
        "MOVIMIENTO": "movementType",
        "DETALLE": "detail",
        "CODIGO DE OPERACION": "operationCode",
        "CANAL": "channel",
        "CODIGO UBICACION": "locationCode",
        "ABONOS/CARGOS": "chargesOrCredits",
        "SALIDA": "amount",
        "JUSTIFICACION": "justification",
        "BANCO": "bank"
      }

      // Convertir los datos del Excel a formato inglés
      const convertedData = excelUploaded.data.map(row => {
        const convertedRow = {}
        
        Object.keys(row).forEach(spanishKey => {
          // Limpiar la key de comillas si las tiene
          const cleanKey = spanishKey.replace(/^['"]+|['"]+$/g, '')
          
          // Obtener el nombre en inglés
          const englishKey = columnMapping[cleanKey]
          
          if (englishKey) {
            let value = row[spanishKey]
            
            // Convertir fechas si es necesario
            if (englishKey === 'operationDate' || englishKey === 'processingDate') {
              if (value && typeof value === 'string') {
                // Convertir formato de fecha DD/MM a YYYY-MM-DD
                const dateParts = value.split('/')
                if (dateParts.length === 2) {
                  const day = dateParts[0].padStart(2, '0')
                  const month = dateParts[1].padStart(2, '0')
                  const year = new Date().getFullYear() // Asumir año actual
                  value = `${year}-${month}-${day}`
                }
              }
            }
            
            // Convertir números si es necesario
            if (englishKey === 'chargesOrCredits' || englishKey === 'amount') {
              value = parseFloat(value) || 0
            }
            
            convertedRow[englishKey] = value
          }
        })
        
        return convertedRow
      })

      await axios.post(`${BACKEND}/api/bulkCreation?collection=conciliacion`, {data: convertedData})
      toast.success("Archivo procesado correctamente")
      
      // Cerrar modal y limpiar estados
      setUploadModal(false)
      setExcelUploaded(null)
      setFileValidation({
        isValid: false,
        missingColumns: [],
        fileName: "",
        fileSize: 0,
        columns: []
      })

      getRecord()
    } catch (error) {
      console.log(error)
      toast.error("Error al procesar el archivo")
    } finally {
      setIsProcessingFile(false)
    }

  }

  const resetFileUpload = () => {
    setExcelUploaded(null)
    setFileValidation({
      isValid: false,
      missingColumns: [],
      fileName: "",
      fileSize: 0,
      columns: []
    })
  }

  useEffect(() => {
    if (headersTableActive.length > 0) {
      localStorage.setItem("activeHeadersConciliacion", JSON.stringify(headersTableActive));
    }
  }, [headersTableActive]);

  useEffect(() => {
    const localActiveHeaders = JSON.parse(localStorage.getItem("activeHeadersConciliacion")) || [];
    if (localActiveHeaders.length > 0) {
      setHeadersTableActive(localActiveHeaders);
    } else {
      setHeadersTableActive(headers);
    }
  }, []);

  return (
    <div className="flex flex-col h-screen w-full">
      
      {/* Upload Modal */}
      <Modal
        activeModal={uploadModal}
        onClose={() => {
          setUploadModal(false)
          resetFileUpload()
        }}
        title="Cargar archivo de conciliación"
        centered
      >
        <div className="flex flex-col gap-6">

          {/* Instrucciones */}
          <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
            <h3 className="font-semibold text-xs text-blue-800 mb-2">Columnas requeridas:</h3>
            <div className="grid grid-cols-3 gap-2 text-xs text-blue-700">
              <div>• FECHA DE OP.</div>
              <div>• FECHA DE PROC.</div>
              <div>• MOVIMIENTO</div>
              <div>• DETALLE</div>
              <div>• CODIGO DE OPERACION</div>
              <div>• CANAL</div>
              <div>• CODIGO UBICACION</div>
              <div>• ABONOS/CARGOS</div>
              <div>• SALIDA</div>
              <div>• JUSTIFICACION</div>
              <div>• BANCO</div>
            </div>
          </div>

          {/* Drag & Drop Area */}
          <div 
            className="flex flex-col items-center justify-center p-8 border-2 border-dashed border-gray-300 rounded-lg cursor-pointer hover:bg-gray-50 transition-colors min-h-[200px]"
            onDragOver={handleDragOver}
            onDrop={handleDrop}
            onClick={() => document.getElementById('file-input').click()}
          >
            <input
              id="file-input"
              type="file"
              accept=".xlsx, .xls, .csv"
              onChange={handleFileInput}
              className="hidden"
            />
            
            {!excelUploaded && !isProcessingFile && (
              <>
                <FaFileExcel className="text-xs text-gray-400 mb-4" />
                <p className="text-center text-gray-600 text-xs mb-2">
                  Arrastra y suelta un archivo Excel aquí
                </p>
                <p className="text-center text-gray-500 text-xs">
                  o haz clic para seleccionar un archivo
                </p>
                <p className="text-center text-gray-400 text-xs mt-2">
                  Formatos soportados: .xlsx, .xls, .csv (máximo 10MB)
                </p>
              </>
            )}

            {/* Loading State */}
            {isProcessingFile && (
              <div className="flex flex-col items-center">
                <FaSpinner className="text-xs text-blue-600 animate-spin mb-4" />
                <span className="text-blue-600 font-medium text-xs">Procesando archivo...</span>
              </div>
            )}

            {/* File Uploaded State */}
            {excelUploaded && !isProcessingFile && (
              <div className="flex flex-col items-center w-full">
                <div className="flex items-center text-blue-600 mb-4">
                  <FaFileExcel className="text-xs mr-2" />
                  <span className="font-medium text-xs">{fileValidation.fileName}</span>
                </div>
                
                {/* File Info */}
                <div className="bg-gray-50 rounded-lg p-3 w-full mb-4">
                  <div className="text-xs text-gray-600">
                    <div className="flex justify-between mb-1">
                      <span>Tamaño:</span>
                      <span>{(fileValidation.fileSize / 1024 / 1024).toFixed(2)} MB</span>
                    </div>
                    <div className="flex justify-between mb-1">
                      <span>Columnas encontradas:</span>
                      <span>{fileValidation.columns.length}</span>
                    </div>
                    <div className="flex justify-between">
                      <span>Filas de datos:</span>
                      <span>{excelUploaded.data.length}</span>
                    </div>
                  </div>
                </div>

                {/* Validation Status */}
                {fileValidation.isValid && (
                  <div className="flex items-center text-green-600 mb-4 bg-green-50 px-4 py-2 rounded-lg">
                    <FaCheckCircle className="mr-2" />
                    <span className="font-medium text-xs">✓ Archivo válido - Todas las columnas requeridas están presentes</span>
                  </div>
                )}

                {fileValidation.missingColumns.length > 0 && (
                  <div className="w-full mb-4">
                    <div className="flex items-center text-red-600 mb-2 bg-red-50 px-4 py-2 rounded-lg">
                      <FaTimes className="mr-2" />
                      <span className="font-medium text-xs">✗ Archivo inválido - Faltan columnas requeridas</span>
                    </div>
                    <div className="bg-red-50 border border-red-200 rounded-lg p-3">
                      <h4 className="font-medium text-red-800 mb-2 text-xs">Columnas faltantes:</h4>
                      <div className="grid grid-cols-2 gap-1">
                        {fileValidation.missingColumns.map((column, index) => (
                          <div key={index} className="text-red-700 text-xs">• {column}</div>
                        ))}
                      </div>
                    </div>
                  </div>
                )}
              </div>
            )}
          </div>

          {/* Action Buttons */}
          <div className="flex justify-center gap-3">
            <Button
              variant="outlined"
              onClick={resetFileUpload}
              disabled={!excelUploaded || isProcessingFile}
              sx={{
                fontSize: "12px",
                fontWeight: "bold",
                fontFamily: "poppins",
                padding: "8px 24px",
                borderRadius: "8px",
                borderColor: "#ff6400",
                color: "#ff6400",
                '&:hover': {
                  backgroundColor: "#ff6400",
                  color: "white",
                }
              }}
            >
              Limpiar
            </Button>
            
            <Button
              variant="contained"
              onClick={uploadExcel}
              disabled={!excelUploaded || !fileValidation.isValid || isProcessingFile}
              sx={{
                fontSize: "12px",
                fontWeight: "bold",
                fontFamily: "poppins",
                padding: "8px 24px",
                backgroundColor: fileValidation.isValid ? "#2e7d32" : "#9e9e9e",
                transition: "all 0.3s ease",
                textTransform: "none",
                borderRadius: "8px",
                minWidth: "150px",
                '&:hover': {
                  backgroundColor: fileValidation.isValid ? "#1b5e20" : "#9e9e9e",
                }
              }}
            >
              {isProcessingFile ? (
                <>
                  <FaSpinner className="mr-2 animate-spin" />
                  Procesando...
                </>
              ) : (
                <>
                  <FaFileExcel className="mr-2" />
                  {fileValidation.isValid ? "Subir archivo" : "Archivo inválido"}
                </>
              )}
            </Button>
          </div>
        </div>
      </Modal>

      {/* Headers */}
      <FilterHeadersDialog
        open={openHeadersFilter}
        setOpen={setOpenHeadersFilter}
        activeCols={headersTableActive}
        setActiveCols={setHeadersTableActive}
      />

      {/* NavBar */}
      <div className="flex flex-row px-4 py-2 bg-gray-100">
        <div className="flex flex-row w-full items-center gap-4 justify-between overflow-x-auto overflow-y-hidden px-4 [&::-webkit-scrollbar]:hidden [-ms-overflow-style:none] [scrollbar-width:none]">
          
          <div className="flex items-center gap-4 py-4">
            <FaChartPie className="text-zinc-700 text-2xl" />
            <h2 className="text-2xl font-bold text-zinc-700">Conciliación</h2>
          </div>

          <div className="flex flex-row gap-4 items-center justify-center">

            <Selector
              title="Vista"
              value={vista}
              setter={setVista}
              options={[
                {label: "Registros", value: "Registros"}, 
                {label: "Resumen", value: "Resumen"}
              ]}
            />

            <div className="relative w-full">
              <input
                type="text"
                placeholder="Buscar marca"
                className="w-[16rem] text-xs text-gray-700 bg-white border border-gray-300 rounded-lg shadow focus:outline-none focus:ring-2 focus:ring-blue-600 focus:border-transparent py-[6px] pr-10"
                value={detailSearch}
                onChange={(e) => setDetailSearch(e.target.value)}
              />
              {detailSearch && (
                <FaTimes
                  onClick={() => handleResetSearch()}
                  className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-500 hover:text-gray-700 cursor-pointer"
                />
              )}
            </div>
            
            <div>
              <Button 
                variant="contained"
                endIcon={<SearchIcon />}
                sx={{
                  fontSize: "12px",
                  fontWeight: "bold",
                  fontFamily: "poppins",
                  padding: "4px 24px",
                  backgroundColor: "#ff6400",
                  transform: "all",
                  animationDuration: "1000",
                  textTransform: "none",
                  borderRadius: "8px",
                  '&:hover': {
                    backgroundColor: "#bb4a03",
                  }
                }}
                onClick={() => handleSearch()}
              >
                Buscar
              </Button>
            </div>

          </div>

        </div>
      </div>

      {/* Metadata */}
      <div className="flex bg-gray-100 mt-0.5 w-full py-1">
        <div className="w-full items-center overflow-x-auto px-4 [&::-webkit-scrollbar]:hidden [-ms-overflow-style:none] [scrollbar-width:none]">
          <div className="flex flex-col gap-2">
            {/* Totales Generales */}
            <div className="flex flex-row justify-center items-center">
              <div className="flex items-center text-xs">
                <span className="border-r border-gray-300 pr-4 pl-4">
                  <span className="font-medium">Registros: </span>{totalDocs}
                </span>
                <span className="border-r border-gray-300 pr-4 pl-4">
                  <span className="font-medium">Total Ingresos S/: </span>{totalIngresos.toLocaleString("en-US", {
                    minimumFractionDigits: 2,
                    maximumFractionDigits: 2
                  })}
                </span>
                <span className="border-r border-gray-300 pr-4 pl-4">
                  <span className="font-medium">Total Egresos S/: </span>{totalEgresos.toLocaleString("en-US", {
                    minimumFractionDigits: 2,
                    maximumFractionDigits: 2
                  })}
                </span>
                <span className="border-r border-gray-300 pr-4 pl-4">
                  <span className="font-medium">Balanza S/: </span>{saldoNeto.toLocaleString("en-US", {
                    minimumFractionDigits: 2,
                    maximumFractionDigits: 2
                  })}
                </span>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Totales por Banco */}
      {Object.keys(ingresosPorBanco).length > 0 && (
        <div className="flex bg-gray-100 mt-0.5 w-full py-1">
          <div className="w-full items-center overflow-x-auto px-4 [&::-webkit-scrollbar]:hidden [-ms-overflow-style:none] [scrollbar-width:none]">
            <div className="flex flex-col gap-2">
              <div className="flex flex-row justify-center items-center">
                <div className="flex items-center text-xs">
                  {Object.entries(ingresosPorBanco).map(([banco, data], index) => (
                    <div key={banco} className={`flex items-center ${index > 0 ? 'border-l border-gray-300 pl-4' : ''}`}>
                      
                      <span className="font-bold text-black pr-2">{banco} </span> |

                      <span className="ml-2 mr-4">
                        <span className="text-black font-semibold">Ingresos: S/: </span><span className="text-green-600">{data.ingresos.toLocaleString("en-US", {
                          minimumFractionDigits: 2,
                          maximumFractionDigits: 2
                        })}</span>
                      </span>

                      <span className="mr-4">
                        <span className="text-black font-semibold">Egresos: S/: </span><span className="text-red-600">{data.egresos.toLocaleString("en-US", {
                          minimumFractionDigits: 2,
                          maximumFractionDigits: 2
                        })}</span>
                      </span>

                      <span className="">
                        <span className="text-black font-semibold">Balanza: S/: </span><span className={`${data.balanza >= 0 ? 'text-green-600' : 'text-red-600'}`}>{data.balanza.toLocaleString("en-US", {
                          minimumFractionDigits: 2,
                          maximumFractionDigits: 2
                        })}</span>
                      </span>

                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Filtros */}
      <div className="flex flex-row items-center justify-between w-full bg-gray-100 mb-0.5 mt-0.5 px-6 gap-2">
        <div className="flex flex-row items-center justify-center w-full">
          <div className="flex flex-row gap-4 items-center justify-center">
            
            <Selector
              title="Mes"
              value={month}
              setter={setMonth}
              options={[{label: "Todos", value: ""}, ...months]}
            />

            <Selector
              title="Año"
              value={year}
              setter={setYear}
              options={[{label: "Todos", value: ""}, ...years]}
            />

            <Selector
              title="Banco"
              value={bank}
              setter={setBank}
              options={[{label: "Todos", value: ""}, ...BanksConciliacion]}
            />

            <FilterButton
              content="Filtrar columnas"
              action={() => setOpenHeadersFilter(true)}
            />

            <Button
              onClick={() => setUploadModal(true)} 
              variant="contained" 
              sx={{
                fontSize: "12px",
                fontWeight: "bold",
                fontFamily: "poppins",
                padding: "4px 24px",
                backgroundColor: "#2e7d32",
                transition: "all 0.3s ease",
                textTransform: "none",
                borderRadius: "8px",
                minWidth: "180px",
                maxWidth: "300px",
                '&:hover': {
                  backgroundColor: "#1b5e20",
                }
              }}
              endIcon={<FaFileExcel size={14} className="text-white"/>}
            >
              <span style={{ 
                whiteSpace: "nowrap", 
                overflow: "hidden", 
                textOverflow: "ellipsis",
                maxWidth: "300px"
              }}>
                Cargar archivo
              </span>
            </Button>

          </div>
        </div>
      </div>
      
      {/* Body */}
      <div className="flex-grow overflow-y-auto">
        {isLoading ? (
          <LoadingScreen />
        ) : (
          (vista === "Registros" && record.length === 0) || (vista === "Resumen" && resumenData.length === 0) ? (
            <NoDataScreen />
          ) : (
            vista === "Registros" ? (
              <table className="w-full text-xs">
                <thead className="sticky top-0 bg-gray-200 z-50">
                  <tr className="text-gray-700 text-center text-xs font-bold z-50">
                    {headers.map((header) => (
                      headersTableActive.find((e) => e.id === header.id)?.show && (
                        <th key={header.id} className="px-2 py-3 text-center bg-[#FE6400] border-x border-gray-300 text-white z-50">
                          {header.name}
                        </th>
                      )
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {record.map((r, i) => {
                    return (
                      <tr key={i} className="text-xs py-1 w-full hover:bg-gray-100 transition-all duration-100 z-10">
                        <ItemRowConciliacion
                          r={r}
                          i={i}
                          headers={headers}
                          headersTableActive={headersTableActive}
                          debounceUpdate={debounceUpdate}
                          setRecord={setRecord}
                        />
                      </tr>
                    )}
                  )}
                </tbody>
              </table>
            ) : (
              <table className="w-full text-xs">
                <thead className="sticky top-0 bg-gray-200 z-50">
                  <tr className="text-gray-700 text-center text-xs font-bold z-50">
                    {headersResume.map((header) => (
                      <th key={header.id} className="px-2 py-3 text-center bg-[#FE6400] border-x border-gray-300 text-white z-50">
                        {header.name}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {resumenData.map((r, i) => {
                    return (
                      <tr key={i} className="text-xs py-1 w-full hover:bg-gray-100 transition-all duration-100 z-10">
                        <ItemRowConciliacionResumen
                          r={r}
                          i={i}
                          headers={headersResume}
                          headersTableActive={headersResume}
                        />
                      </tr>
                    )}
                  )}
                </tbody>
              </table>
            )
          )
        )}
      </div>
  
    </div>
  )
}

export default Conciliacion;